import React, { FunctionComponent } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { Button, Card } from '@shopify/polaris';
import { ChevronDownMinor, ChevronUpMinor } from '@shopify/polaris-icons';
import { RemoveButtonText } from '@ui/common';
import styled from 'styled-components';

interface DraggableItemProps {
  onRemove?(option: string): void;
  reOrderDraggableItems(sourceIndex: number, destinationIndex: number): void;
  disableRemove?: boolean;
  index: number;
  isDragDisabled?: boolean;
  children: React.ReactNode;
  totalChildren: number;
  id: string;
}

const ItemContainer = styled.div`
  display: flex;
  margin: 1rem 0;
  padding: 1.7rem;
  align-items: center;
`;

const DraggableItemContent = styled.div`
  padding-left: 1.5rem;
  width: 100%;
`;

const DraggableButtonGroup = styled.div`
  display: flex;
  flex-direction: column;

  .Polaris-Button {
    min-height: 1.5rem;
    min-width: 1.6rem;

    &:focus {
      outline: none;
    }
  }

  .Polaris-Icon {
    width: 100%;
    height: 100%;
    display: flex;
  }

  svg {
    height: 8px;
    width: auto;
  }

  .Polaris-Button--iconOnly {
    padding: 0;
  }
`;

const DraggableButtonContainer = styled.div<{
  position?: 'top' | 'bottom';
}>`
  margin-top: ${(props) =>
    props.position === 'bottom' ? '-.55rem' : undefined};

  .Polaris-Button {
    border-bottom: ${(props) =>
      props.position === 'top' ? 'none' : undefined};
    border-top-left-radius: ${(props) =>
      props.position === 'top' ? '.5rem' : undefined};
    border-top-right-radius: ${(props) =>
      props.position === 'top' ? '.5rem' : undefined};
    border-bottom-left-radius: ${(props) =>
      props.position === 'bottom' ? '.5rem' : undefined};
    border-bottom-right-radius: ${(props) =>
      props.position === 'bottom' ? '.5rem' : undefined};
    box-shadow: none;
  }
`;

const DraggableItemDeleteButton = styled.div`
  margin-left: auto;
  padding: 0.5rem;
`;

const DraggableItem: FunctionComponent<DraggableItemProps> = (props) => {
  const {
    children,
    disableRemove,
    id,
    index,
    isDragDisabled,
    onRemove,
    reOrderDraggableItems,
    totalChildren,
  } = props;

  return (
    <Draggable draggableId={id} index={index} isDragDisabled={isDragDisabled}>
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <Card>
            <ItemContainer>
              {!isDragDisabled && (
                <DraggableButtonGroup>
                  <DraggableButtonContainer position="top">
                    <Button
                      disabled={index === 0}
                      onClick={() => reOrderDraggableItems(index, index - 1)}
                      size="slim"
                      icon={ChevronUpMinor}
                    />
                  </DraggableButtonContainer>
                  <DraggableButtonContainer position="bottom">
                    <Button
                      disabled={index === totalChildren - 1}
                      onClick={() => reOrderDraggableItems(index, index + 1)}
                      size="slim"
                      icon={ChevronDownMinor}
                    />
                  </DraggableButtonContainer>
                </DraggableButtonGroup>
              )}

              <DraggableItemContent>{children}</DraggableItemContent>
              {!disableRemove && onRemove && (
                <DraggableItemDeleteButton onClick={() => onRemove(id)}>
                  <RemoveButtonText>Remove</RemoveButtonText>
                </DraggableItemDeleteButton>
              )}
            </ItemContainer>
          </Card>
        </div>
      )}
    </Draggable>
  );
};

export default DraggableItem;
